import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  BookOpenText,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  CircleAlert,
  Crown,
  Dice5,
  Feather,
  Flame,
  Hammer,
  Heart,
  Lock,
  LogOut,
  Minus,
  Moon,
  Plus,
  RotateCcw,
  Save,
  Settings,
  Shield,
  Skull,
  Swords,
  Sun,
  Trash2,
  UserPlus,
  Waves,
  Wheat,
  Wine,
  Zap,
} from "lucide-react";
import "@/App.css";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Toaster, toast } from "@/components/ui/sonner";
import { GODS } from "@/data/gods";
import { MONSTER_AREAS } from "@/data/monsters";

const STORAGE_PREFIX = "arcaeus_god_";
const OWNER_TOKEN_KEY = "arcaeus_owner_token";
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_COMBATANT = {
  name: "",
  kind: "player",
  initiative: 0,
  hp: "",
  statuses: "",
  notes: "",
};

const EMPTY_BATTLE = {
  id: "active",
  name: "Active Battle",
  round: 1,
  current_turn_index: 0,
  combatants: [],
};

const DIVINITY_RULES = [
  { id: "starting", title: "25 Starting Divinity", text: "Begin every hero sheet with divine fuel ready." },
  { id: "round", title: "+3 Each New Round", text: "At the top of every round after the first, all players gain +3 divinity." },
  { id: "slain", title: "+2 Per Slain Foe", text: "Every monster killed grants 2 Divinity to the person who killed it." },
];

const STATUS_EFFECTS = [
  { id: "stunned", name: "Stunned", description: "Lose your next Action.", tone: "border-sky-300/30 bg-sky-300/10 text-sky-100" },
  { id: "exposed", name: "Exposed", description: "Next hit against you deals +1d6 damage.", tone: "border-amber-300/35 bg-amber-300/10 text-amber-100" },
  { id: "guard", name: "Guard", description: "Temporary shield HP — absorbed before real HP.", tone: "border-emerald-300/30 bg-emerald-300/10 text-emerald-100" },
  { id: "burning", name: "Burning", description: "Take 1d4 fire damage at start of turn.", tone: "border-orange-300/35 bg-orange-300/10 text-orange-100" },
  { id: "poisoned", name: "Poisoned", description: "Take 1d4 poison damage at end of turn.", tone: "border-lime-300/30 bg-lime-300/10 text-lime-100" },
  { id: "charmed", name: "Charmed", description: "Cannot attack the charmer unless harmed by them.", tone: "border-pink-300/30 bg-pink-300/10 text-pink-100" },
  { id: "fear", name: "Fear", description: "Must flee, or attack with disadvantage.", tone: "border-violet-300/30 bg-violet-300/10 text-violet-100" },
  { id: "rooted", name: "Rooted", description: "Cannot move. May still attack.", tone: "border-green-300/30 bg-green-300/10 text-green-100" },
  { id: "asleep", name: "Asleep", description: "Lose your turn. Wake on damage taken.", tone: "border-indigo-300/30 bg-indigo-300/10 text-indigo-100" },
  { id: "madness", name: "Madness", description: "When Psychic HP falls to zero, roll d20. On a failed roll, roll d6 next turn: low results are manageable, severe results can cause disadvantage, self-damage, or losing an Action.", tone: "border-fuchsia-300/35 bg-fuchsia-300/10 text-fuchsia-100" },
];

const DEFAULT_STATUS_EFFECTS = STATUS_EFFECTS;

const STATUS_EFFECT_BY_ID = Object.fromEntries(STATUS_EFFECTS.map((effect) => [effect.id, effect]));

const applyContentOverrides = (overrides = {}) => {
  const gods = GODS.map((god) => ({
    ...god,
    ...(overrides.gods?.[god.id] || {}),
    abilities: overrides.gods?.[god.id]?.abilities || god.abilities,
  }));
  const statuses = STATUS_EFFECTS.map((status) => ({
    ...status,
    ...(overrides.statuses?.[status.id] || {}),
  }));
  return { gods, statuses };
};

const mapGodsById = (gods) => Object.fromEntries(gods.map((god) => [god.id, god]));

const GOD_ICON_MAP = {
  zeus: Zap,
  athena: Shield,
  poseidon: Waves,
  hera: Crown,
  demeter: Wheat,
  apollo: Sun,
  artemis: Moon,
  hephaestus: Hammer,
  aphrodite: Heart,
  ares: Swords,
  hermes: Feather,
  dionysus: Wine,
};

const toKey = (name) => `${STORAGE_PREFIX}${name.toLowerCase().trim().replace(/\s+/g, "_")}`;

const loadPlayer = (name) => {
  try {
    const value = window.sessionStorage.getItem(toKey(name));
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const savePlayer = (name, godId, level) => {
  window.sessionStorage.setItem(toKey(name), JSON.stringify({ godId, level, lockedAt: Date.now() }));
};

const clearPlayer = (name) => window.sessionStorage.removeItem(toKey(name));

const actionTone = (action = "") => {
  const value = action.toLowerCase();
  if (value.includes("reaction")) return "border-sky-400/40 text-sky-200 bg-sky-400/10";
  if (value.includes("minor")) return "border-amber-300/40 text-amber-200 bg-amber-300/10";
  return "border-red-400/40 text-red-200 bg-red-400/10";
};

const clampLevel = (level) => Math.max(1, Math.min(5, Number.parseInt(level, 10) || 1));

const parseStatuses = (value = "") => (
  value
    .split(",")
    .map((status) => status.trim())
    .filter(Boolean)
);

const formatStatuses = (statuses) => statuses.join(", ");

const toggleStatusValue = (currentValue, statusName) => {
  const statuses = parseStatuses(currentValue);
  const hasStatus = statuses.includes(statusName);
  return formatStatuses(hasStatus
    ? statuses.filter((status) => status !== statusName)
    : [...statuses, statusName]);
};

function App() {
  const [screen, setScreen] = useState("name");
  const [nameInput, setNameInput] = useState("");
  const levelInput = 1;
  const [playerName, setPlayerName] = useState("");
  const [playerLevel, setPlayerLevel] = useState(1);
  const [lockedGod, setLockedGod] = useState(null);
  const [pendingGod, setPendingGod] = useState(null);
  const [revealingGod, setRevealingGod] = useState(null);
  const [revealPhase, setRevealPhase] = useState("idle");
  const [confirmReroll, setConfirmReroll] = useState(false);
  const [activeStatusId, setActiveStatusId] = useState(STATUS_EFFECTS[0].id);
  const [ownerToken, setOwnerToken] = useState(() => window.sessionStorage.getItem(OWNER_TOKEN_KEY) || "");
  const [contentOverrides, setContentOverrides] = useState({ gods: {}, statuses: {} });
  const nameRef = useRef(null);

  const content = useMemo(() => applyContentOverrides(contentOverrides), [contentOverrides]);
  const visibleGods = content.gods;
  const visibleStatuses = content.statuses;
  const visibleGodMap = useMemo(() => mapGodsById(visibleGods), [visibleGods]);
  const visibleStatusMap = useMemo(() => Object.fromEntries(visibleStatuses.map((effect) => [effect.id, effect])), [visibleStatuses]);
  const displayLockedGod = lockedGod ? visibleGodMap[lockedGod.id] || lockedGod : null;
  const availableUpgrades = useMemo(() => Math.max(0, playerLevel - 1), [playerLevel]);
  const activeStatus = useMemo(
    () => visibleStatusMap[activeStatusId] || visibleStatuses[0],
    [activeStatusId, visibleStatusMap, visibleStatuses],
  );

  useEffect(() => {
    async function loadOverrides() {
      try {
        const response = await fetch(`${API_BASE}/content/overrides`);
        if (!response.ok) throw new Error("Could not load edited content.");
        const data = await response.json();
        setContentOverrides({ gods: data.gods || {}, statuses: data.statuses || {} });
      } catch (error) {
        toast.error(error.message || "Could not load edited content.");
      }
    }
    loadOverrides();
  }, []);

  const handleSubmit = () => {
    const name = nameInput.trim();
    if (!name) {
      nameRef.current?.focus();
      toast.error("Enter a player name before consulting the registry.");
      return;
    }

    const level = clampLevel(levelInput);
    const saved = loadPlayer(name);
    setPlayerName(name);
    setPlayerLevel(saved?.godId && level === 1 ? saved.level || 1 : level);

    if (saved?.godId && visibleGodMap[saved.godId]) {
      const resolvedLevel = saved?.godId && level === 1 ? saved.level || 1 : level;
      savePlayer(name, saved.godId, resolvedLevel);
      setLockedGod(visibleGodMap[saved.godId]);
      setScreen("locked");
      toast.success(`${visibleGodMap[saved.godId].name} has been restored from the registry.`);
      return;
    }

    setLockedGod(null);
    setScreen("select");
  };

  const handleDrawRandom = () => {
    setConfirmReroll(false);
    const randomGod = visibleGods[Math.floor(Math.random() * visibleGods.length)];
    setRevealingGod(randomGod);
    setRevealPhase("back");
    window.setTimeout(() => setRevealPhase("flipped"), 650);
    window.setTimeout(() => setRevealPhase("zooming"), 1950);
    window.setTimeout(() => {
      savePlayer(playerName, randomGod.id, playerLevel);
      setLockedGod(randomGod);
      setRevealingGod(null);
      setRevealPhase("idle");
      setScreen("locked");
      toast.success(`${randomGod.name} has answered the random draw.`);
    }, 2850);
  };

  const handleOpenPatron = (god) => {
    setConfirmReroll(false);
    setPendingGod(god);
  };

  const handleConfirmGod = () => {
    if (!pendingGod) return;
    savePlayer(playerName, pendingGod.id, playerLevel);
    setLockedGod(pendingGod);
    setPendingGod(null);
    setScreen("locked");
    toast.success(`${pendingGod.name} is now locked as your patron.`);
  };

  const handleLevelChange = (nextLevel) => {
    const level = clampLevel(nextLevel);
    setPlayerLevel(level);
    if (lockedGod && playerName) savePlayer(playerName, lockedGod.id, level);
  };

  const handleLevelDelta = (delta) => {
    setPlayerLevel((currentLevel) => {
      const level = clampLevel(currentLevel + delta);
      if (lockedGod && playerName) savePlayer(playerName, lockedGod.id, level);
      return level;
    });
  };

  const handleReroll = () => {
    clearPlayer(playerName);
    setPendingGod(null);
    setLockedGod(null);
    setConfirmReroll(false);
    setScreen("select");
    toast.message("The old oath has been released. Choose a new patron.");
  };

  const handleOpenReroll = () => {
    setPendingGod(null);
    setConfirmReroll(true);
  };

  const handleOwnerAuthenticated = (token) => {
    window.sessionStorage.setItem(OWNER_TOKEN_KEY, token);
    setOwnerToken(token);
    setScreen("owner");
  };

  const handleOwnerLogout = () => {
    window.sessionStorage.removeItem(OWNER_TOKEN_KEY);
    setOwnerToken("");
    setScreen("name");
    toast.message("Owner session closed.");
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-[#080808] text-zinc-100 selection:bg-[#D4AF37] selection:text-black" data-testid="arcaeus-app-shell">
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_12%_10%,rgba(212,175,55,0.18),transparent_30%),radial-gradient(circle_at_85%_20%,rgba(91,163,208,0.10),transparent_34%),linear-gradient(135deg,rgba(8,9,12,0.95),rgba(2,3,6,0.98))]" />
      <div className="fixed inset-0 opacity-[0.12] [background-image:linear-gradient(rgba(255,255,255,.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.08)_1px,transparent_1px)] [background-size:72px_72px]" />
      <div className="relative mx-auto flex min-h-screen w-full max-w-7xl flex-col px-4 py-5 sm:px-6 lg:px-10">
        {screen === "name" && (
          <NameScreen
            nameInput={nameInput}
            setNameInput={setNameInput}
            levelInput={levelInput}
            handleSubmit={handleSubmit}
            nameRef={nameRef}
            onOwnerOpen={() => setScreen(ownerToken ? "owner" : "owner-login")}
          />
        )}

        {screen === "owner-login" && (
          <OwnerLoginScreen
            onBack={() => setScreen("name")}
            onAuthenticated={handleOwnerAuthenticated}
          />
        )}

        {screen === "owner" && (
          <OwnerBattleScreen
            ownerToken={ownerToken}
            onBack={() => setScreen("name")}
            onLogout={handleOwnerLogout}
            visibleGods={visibleGods}
            visibleStatuses={visibleStatuses}
            setContentOverrides={setContentOverrides}
            onSessionExpired={() => {
              window.sessionStorage.removeItem(OWNER_TOKEN_KEY);
              setOwnerToken("");
              setScreen("owner-login");
            }}
          />
        )}

        {screen === "select" && (
          <SelectScreen
            playerName={playerName}
            playerLevel={playerLevel}
            onBack={() => setScreen("name")}
            handleDrawRandom={handleDrawRandom}
            handleOpenPatron={handleOpenPatron}
            gods={visibleGods}
          />
        )}

        {screen === "locked" && displayLockedGod && (
          <LockedScreen
            playerName={playerName}
            playerLevel={playerLevel}
            lockedGod={displayLockedGod}
            activeStatus={activeStatus}
            statuses={visibleStatuses}
            visibleGods={visibleGods}
            visibleStatuses={visibleStatuses}
            availableUpgrades={availableUpgrades}
            onBack={() => setScreen("name")}
            handleLevelChange={handleLevelChange}
            handleLevelDelta={handleLevelDelta}
            handleOpenReroll={handleOpenReroll}
            setActiveStatusId={setActiveStatusId}
            ownerToken={ownerToken}
            setContentOverrides={setContentOverrides}
          />
        )}
      </div>

      <RandomGodReveal god={revealingGod} phase={revealPhase} />

      <Dialog open={Boolean(pendingGod)} onOpenChange={(open) => !open && setPendingGod(null)}>
        <DialogContent closeTestId="confirm-patron-close-button" className="border-[#D4AF37]/45 bg-black/85 text-zinc-100 backdrop-blur-2xl sm:max-w-md" data-testid="confirm-patron-dialog">
          {pendingGod && (
            <>
              <DialogHeader className="text-center sm:text-center">
                <GodSymbol god={pendingGod} testId="confirm-patron-symbol" className="mx-auto mb-3 h-20 w-20 rounded-3xl" iconClassName="h-10 w-10" />
                <DialogTitle className="font-serif text-4xl text-zinc-100" data-testid="confirm-patron-title">Lock in {pendingGod.name}?</DialogTitle>
                <DialogDescription className="text-base leading-7 text-zinc-400" data-testid="confirm-patron-description">{pendingGod.domain}</DialogDescription>
              </DialogHeader>
              <DialogFooter className="mt-3 gap-3 sm:justify-center sm:space-x-0">
                <Button data-testid="confirm-patron-button" onClick={handleConfirmGod} className="border border-[#D4AF37]/70 bg-[#D4AF37] text-black hover:bg-[#E5C158]">
                  Lock in patron
                </Button>
                <Button data-testid="cancel-patron-button" onClick={() => setPendingGod(null)} className="border border-[#D4AF37]/40 bg-transparent text-zinc-200 hover:bg-white/10">
                  Choose again
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={confirmReroll} onOpenChange={setConfirmReroll}>
        <DialogContent closeTestId="reroll-dialog-close-button" className="border-[#D4AF37]/45 bg-black/85 text-zinc-100 backdrop-blur-2xl sm:max-w-md" data-testid="reroll-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-4xl text-zinc-100" data-testid="reroll-dialog-title">Release this patron?</DialogTitle>
            <DialogDescription className="text-base leading-7 text-zinc-400" data-testid="reroll-dialog-description">
              Your saved oath for {playerName || "this player"} will be cleared, and you can choose again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-3 sm:space-x-0">
            <Button data-testid="confirm-reroll-button" onClick={handleReroll} className="border border-[#D4AF37]/45 bg-red-500 text-white hover:bg-red-400">
              Confirm reroll
            </Button>
            <Button data-testid="cancel-reroll-button" onClick={() => setConfirmReroll(false)} className="border border-[#D4AF37]/40 bg-transparent text-zinc-200 hover:bg-white/10">
              Keep patron
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Toaster position="bottom-center" richColors />
    </main>
  );
}

function NameScreen({ nameInput, setNameInput, levelInput, handleSubmit, nameRef, onOwnerOpen }) {
  return (
    <section className="grid min-h-[calc(100vh-40px)] items-center gap-6 xl:grid-cols-[1.15fr_0.85fr]" data-testid="name-entry-screen">
      <div className="space-y-5 border border-[#D4AF37]/35 bg-black/35 p-4 backdrop-blur-sm sm:p-6" data-testid="intro-layout-panel">
        <TutorialTitle />
        <CombatPanel title="Divinity Rules" testId="divinity-rules-panel">
          <div className="grid gap-3 md:grid-cols-3">
            {DIVINITY_RULES.map((rule) => (
              <div key={rule.id} className="rounded-2xl border border-[#D4AF37]/15 bg-[#D4AF37]/5 p-4" data-testid={`divinity-rule-${rule.id}`}>
                <h3 className="font-serif text-2xl text-[#D4AF37]" data-testid={`divinity-rule-${rule.id}-title`}>{rule.title}</h3>
                <p className="mt-2 text-sm leading-6 text-zinc-500" data-testid={`divinity-rule-${rule.id}-text`}>{rule.text}</p>
              </div>
            ))}
          </div>
        </CombatPanel>
        <Button data-testid="open-owner-section-button" onClick={onOwnerOpen} className="w-full border border-[#D4AF37]/35 bg-white/[0.03] py-6 text-[#D4AF37] hover:bg-[#D4AF37]/10">
          <Lock className="h-4 w-4" /> Owner Battle Tools
        </Button>
      </div>

      <div className="border border-[#D4AF37]/35 bg-black/55 p-4 shadow-2xl shadow-black/40 backdrop-blur-xl sm:p-6" data-testid="entry-card">
        <div className="border border-[#D4AF37]/35 bg-[#141414]/90 p-6 sm:p-8" data-testid="entry-inner-panel">
          <div className="mb-8 flex items-center justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.32em] text-zinc-500" data-testid="entry-card-label">Begin registry</p>
              <h2 className="mt-2 font-serif text-4xl text-zinc-100" data-testid="entry-card-title">Draw god</h2>
            </div>
            <div className="grid h-14 w-14 place-items-center border border-[#D4AF37]/45 bg-[#D4AF37]/10 text-[#D4AF37]" data-testid="entry-card-icon">
              <Crown className="h-7 w-7" />
            </div>
          </div>

          <div className="space-y-6">
            <label className="block" data-testid="name-input-label">
              <span className="mb-2 block text-xs uppercase tracking-[0.28em] text-zinc-500">Player name</span>
              <Input
                ref={nameRef}
                data-testid="player-name-input"
                value={nameInput}
                onChange={(event) => setNameInput(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && handleSubmit()}
                placeholder="Enter your name"
                className="h-14 border-white/10 bg-black/30 px-5 font-sans text-lg text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-[#D4AF37]/60"
              />
            </label>

            <Button data-testid="find-god-button" onClick={handleSubmit} className="h-13 w-full border border-[#D4AF37]/70 bg-[#D4AF37] py-6 text-black hover:bg-[#E5C158]">
              Begin turn order
              <ChevronsUpDown className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

function OwnerLoginScreen({ onBack, onAuthenticated }) {
  const [passcode, setPasscode] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async () => {
    if (!passcode.trim()) {
      toast.error("Enter the owner passcode.");
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/owner/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ passcode }),
      });
      if (!response.ok) throw new Error("Incorrect owner passcode");
      const data = await response.json();
      onAuthenticated(data.access_token);
      toast.success("Owner tools unlocked.");
    } catch (error) {
      toast.error(error.message || "Unable to unlock owner tools.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="grid min-h-[calc(100vh-40px)] place-items-center" data-testid="owner-login-screen">
      <div className="w-full max-w-xl border border-[#D4AF37]/40 bg-[#111318]/90 p-7" data-testid="owner-login-card">
        <p className="text-xs uppercase tracking-[0.34em] text-[#D4AF37]" data-testid="owner-login-kicker">Owner Only</p>
        <h1 className="mt-3 font-serif text-5xl text-zinc-100" data-testid="owner-login-title">Battle Tools</h1>
        <p className="mt-3 text-sm leading-6 text-zinc-500" data-testid="owner-login-description">Enter the owner passcode to manage active battle turn order, rounds, HP, status notes, players, and monsters.</p>
        <Input
          data-testid="owner-passcode-input"
          type="password"
          value={passcode}
          onChange={(event) => setPasscode(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && handleLogin()}
          placeholder="Owner passcode"
          className="mt-7 h-14 border-[#D4AF37]/35 bg-black/30 text-zinc-100"
        />
        <div className="mt-5 flex flex-col gap-3 sm:flex-row">
          <Button data-testid="owner-login-submit-button" onClick={handleLogin} disabled={isLoading} className="flex-1 border border-[#D4AF37]/70 bg-[#D4AF37] py-6 text-black hover:bg-[#E5C158]">
            <Lock className="h-4 w-4" /> {isLoading ? "Unlocking..." : "Unlock Owner Tools"}
          </Button>
          <Button data-testid="owner-login-back-button" onClick={onBack} className="border border-[#D4AF37]/35 bg-white/[0.03] py-6 text-zinc-200 hover:bg-white/10">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        </div>
      </div>
    </section>
  );
}

function OwnerBattleScreen({ ownerToken, onBack, onLogout, visibleGods, visibleStatuses, setContentOverrides, onSessionExpired }) {
  const [battle, setBattle] = useState(EMPTY_BATTLE);
  const [draftCombatant, setDraftCombatant] = useState(EMPTY_COMBATANT);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const sortedCombatants = useMemo(
    () => [...battle.combatants].sort((a, b) => (Number(b.initiative) || 0) - (Number(a.initiative) || 0)),
    [battle.combatants],
  );
  const currentCombatant = sortedCombatants[battle.current_turn_index] || null;

  const authHeaders = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${ownerToken}`,
  }), [ownerToken]);

  const handleAuthFailure = () => {
    toast.error("Owner session expired. Please enter the passcode again.");
    onSessionExpired();
  };

  const saveBattle = async (nextBattle = battle) => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_BASE}/owner/battle`, {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify(nextBattle),
      });
      if (response.status === 401) {
        handleAuthFailure();
        return;
      }
      if (!response.ok) throw new Error("Could not save active battle.");
      const data = await response.json();
      setBattle({ ...EMPTY_BATTLE, ...data, combatants: data.combatants || [] });
      toast.success("Active battle saved.");
    } catch (error) {
      toast.error(error.message || "Could not save active battle.");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!ownerToken) return;

    let isActive = true;

    async function loadActiveBattle() {
      try {
        const response = await fetch(`${API_BASE}/owner/battle`, { headers: authHeaders });
        if (response.status === 401) {
          handleAuthFailure();
          return;
        }
        if (!response.ok) throw new Error("Could not load active battle.");
        const data = await response.json();
        if (isActive) setBattle({ ...EMPTY_BATTLE, ...data, combatants: data.combatants || [] });
      } catch (error) {
        if (isActive) toast.error(error.message || "Could not load active battle.");
      } finally {
        if (isActive) setIsLoading(false);
      }
    }

    loadActiveBattle();

    return () => {
      isActive = false;
    };
  }, [authHeaders, ownerToken]);

  const updateBattle = (updates) => setBattle((current) => ({ ...current, ...updates }));

  const addCombatant = () => {
    const name = draftCombatant.name.trim();
    if (!name) {
      toast.error("Add a name before adding to turn order.");
      return;
    }
    const nextCombatant = {
      ...draftCombatant,
      id: crypto.randomUUID(),
      name,
      initiative: Number(draftCombatant.initiative) || 0,
    };
    setBattle((current) => ({
      ...current,
      combatants: [...current.combatants, nextCombatant],
    }));
    setDraftCombatant(EMPTY_COMBATANT);
  };

  const addMonsterTemplate = (monster) => {
    setBattle((current) => ({
      ...current,
      combatants: [
        ...current.combatants,
        {
          id: crypto.randomUUID(),
          name: monster.name,
          kind: "monster",
          initiative: monster.initiative || 0,
          hp: monster.hp || "",
          statuses: monster.statuses || "",
          notes: monster.notes || "",
        },
      ],
    }));
    toast.success(`${monster.name} added to initiative.`);
  };

  const updateCombatant = (id, field, value) => {
    setBattle((current) => ({
      ...current,
      combatants: current.combatants.map((combatant) => (
        combatant.id === id ? { ...combatant, [field]: field === "initiative" ? Number(value) || 0 : value } : combatant
      )),
    }));
  };

  const removeCombatant = (id) => {
    setBattle((current) => {
      const nextCombatants = current.combatants.filter((combatant) => combatant.id !== id);
      return {
        ...current,
        combatants: nextCombatants,
        current_turn_index: Math.min(current.current_turn_index, Math.max(nextCombatants.length - 1, 0)),
      };
    });
  };

  const stepTurn = (direction) => {
    if (!sortedCombatants.length) return;
    setBattle((current) => {
      const nextIndex = current.current_turn_index + direction;
      if (nextIndex >= sortedCombatants.length) {
        return { ...current, current_turn_index: 0, round: current.round + 1 };
      }
      if (nextIndex < 0) {
        return { ...current, current_turn_index: sortedCombatants.length - 1, round: Math.max(1, current.round - 1) };
      }
      return { ...current, current_turn_index: nextIndex };
    });
  };

  const resetBattle = () => {
    setBattle(EMPTY_BATTLE);
    setDraftCombatant(EMPTY_COMBATANT);
    toast.message("Battle board cleared. Save to replace the active battle.");
  };

  return (
    <section className="py-6" data-testid="owner-battle-screen">
      <header className="flex flex-col gap-4 border-b border-[#D4AF37]/25 pb-5 sm:flex-row sm:items-center sm:justify-between" data-testid="owner-header">
        <div>
          <p className="text-xs uppercase tracking-[0.35em] text-[#D4AF37]" data-testid="owner-kicker">Owner Tools</p>
          <h1 className="mt-2 font-serif text-5xl text-zinc-100" data-testid="owner-title">Battle Turn Order</h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button data-testid="save-battle-button" onClick={() => saveBattle()} disabled={isSaving} className="border border-[#D4AF37]/70 bg-[#D4AF37] text-black hover:bg-[#E5C158]">
            <Save className="h-4 w-4" /> {isSaving ? "Saving..." : "Save Battle"}
          </Button>
          <Button data-testid="owner-back-button" onClick={onBack} className="border border-[#D4AF37]/35 bg-white/[0.03] text-zinc-200 hover:bg-white/10">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <Button data-testid="owner-logout-button" onClick={onLogout} className="border border-[#D4AF37]/35 bg-red-400/5 text-red-200 hover:bg-red-400/10">
            <LogOut className="h-4 w-4" /> Lock
          </Button>
        </div>
      </header>

      {isLoading ? (
        <div className="mt-8 border border-[#D4AF37]/35 bg-[#111318]/85 p-8 text-zinc-400" data-testid="owner-loading-state">Loading active battle...</div>
      ) : (
        <div className="mt-8 grid gap-6 xl:grid-cols-[0.7fr_1.3fr]">
          <OwnerControlPanel
            battle={battle}
            draftCombatant={draftCombatant}
            setDraftCombatant={setDraftCombatant}
            updateBattle={updateBattle}
            addCombatant={addCombatant}
            addMonsterTemplate={addMonsterTemplate}
            currentCombatant={currentCombatant}
            stepTurn={stepTurn}
            resetBattle={resetBattle}
            visibleStatuses={visibleStatuses}
          />
          <TurnOrderTable
            sortedCombatants={sortedCombatants}
            currentCombatant={currentCombatant}
            updateCombatant={updateCombatant}
            removeCombatant={removeCombatant}
            visibleStatuses={visibleStatuses}
          />
          <OwnerContentTools
            ownerToken={ownerToken}
            visibleGods={visibleGods}
            visibleStatuses={visibleStatuses}
            setContentOverrides={setContentOverrides}
          />
        </div>
      )}
    </section>
  );
}

function OwnerControlPanel({ battle, draftCombatant, setDraftCombatant, updateBattle, addCombatant, addMonsterTemplate, currentCombatant, stepTurn, resetBattle, visibleStatuses }) {
  const [isAddCombatantOpen, setIsAddCombatantOpen] = useState(false);

  return (
    <aside className="space-y-5" data-testid="owner-control-panel">
      <div className="border border-[#D4AF37]/35 bg-[#111318]/85 p-5" data-testid="battle-summary-panel">
        <label className="block" data-testid="battle-name-label">
          <span className="mb-2 block text-xs uppercase tracking-[0.28em] text-zinc-500">Battle name</span>
          <Input data-testid="battle-name-input" value={battle.name} onChange={(event) => updateBattle({ name: event.target.value })} className="border-[#D4AF37]/35 bg-black/30 text-zinc-100" />
        </label>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="border border-[#D4AF37]/35 bg-black/25 p-4" data-testid="battle-round-panel">
            <p className="text-xs uppercase tracking-[0.24em] text-zinc-600">Round</p>
            <div className="mt-2 flex items-center gap-2">
              <Button data-testid="decrease-round-button" onClick={() => updateBattle({ round: Math.max(1, battle.round - 1) })} className="h-9 w-9 border border-[#D4AF37]/35 bg-white/[0.03] p-0"><Minus className="h-4 w-4" /></Button>
              <span className="font-serif text-3xl text-[#D4AF37]" data-testid="battle-round-value">{battle.round}</span>
              <Button data-testid="increase-round-button" onClick={() => updateBattle({ round: battle.round + 1 })} className="h-9 w-9 border border-[#D4AF37]/35 bg-white/[0.03] p-0"><Plus className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="border border-[#D4AF37]/35 bg-black/25 p-4" data-testid="current-turn-panel">
            <p className="text-xs uppercase tracking-[0.24em] text-zinc-600">Current turn</p>
            <p className="mt-2 font-serif text-2xl text-zinc-100" data-testid="current-turn-name">{currentCombatant?.name || "None"}</p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <Button data-testid="previous-turn-button" onClick={() => stepTurn(-1)} className="border border-[#D4AF37]/35 bg-white/[0.03] py-6 text-zinc-200 hover:bg-white/10">Previous</Button>
          <Button data-testid="next-turn-button" onClick={() => stepTurn(1)} className="border border-[#D4AF37]/70 bg-[#D4AF37] py-6 text-black hover:bg-[#E5C158]">Next Turn</Button>
        </div>
      </div>

      <div className="border border-[#D4AF37]/35 bg-[#111318]/85" data-testid="add-combatant-panel">
        <button
          type="button"
          data-testid="toggle-add-combatant-panel-button"
          onClick={() => setIsAddCombatantOpen((isOpen) => !isOpen)}
          aria-expanded={isAddCombatantOpen}
          className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-[background-color] duration-200 hover:bg-white/[0.03]"
        >
          <span>
            <span className="block text-xs uppercase tracking-[0.32em] text-[#D4AF37]" data-testid="add-combatant-title">Add Combatant</span>
            <span className="mt-1 block text-xs text-zinc-500" data-testid="add-combatant-summary">Create a player or monster entry</span>
          </span>
          <span className="text-[#D4AF37]" data-testid="add-combatant-collapse-icon">
            {isAddCombatantOpen ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </span>
        </button>
        {isAddCombatantOpen && (
          <div className="grid gap-3 border-t border-[#D4AF37]/25 p-5" data-testid="add-combatant-form">
            <Input data-testid="combatant-name-input" placeholder="Name" value={draftCombatant.name} onChange={(event) => setDraftCombatant({ ...draftCombatant, name: event.target.value })} className="border-[#D4AF37]/35 bg-black/30 text-zinc-100" />
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="combatant-kind-select" value={draftCombatant.kind} onChange={(event) => setDraftCombatant({ ...draftCombatant, kind: event.target.value })} className="border border-[#D4AF37]/35 bg-black/30 px-3 py-2 text-zinc-100">
                <option value="player">Player</option>
                <option value="monster">Monster</option>
              </select>
              <Input data-testid="combatant-initiative-input" type="number" placeholder="Initiative" value={draftCombatant.initiative} onChange={(event) => setDraftCombatant({ ...draftCombatant, initiative: event.target.value })} className="border-[#D4AF37]/35 bg-black/30 text-zinc-100" />
            </div>
            <Input data-testid="combatant-hp-input" placeholder="HP e.g. 32/40" value={draftCombatant.hp} onChange={(event) => setDraftCombatant({ ...draftCombatant, hp: event.target.value })} className="border-[#D4AF37]/35 bg-black/30 text-zinc-100" />
            <StatusPicker
              testId="combatant-statuses-picker"
              value={draftCombatant.statuses}
              statuses={visibleStatuses}
              onChange={(statuses) => setDraftCombatant({ ...draftCombatant, statuses })}
            />
            <Input data-testid="combatant-notes-input" placeholder="Notes" value={draftCombatant.notes} onChange={(event) => setDraftCombatant({ ...draftCombatant, notes: event.target.value })} className="border-[#D4AF37]/35 bg-black/30 text-zinc-100" />
            <Button data-testid="add-combatant-button" onClick={addCombatant} className="border border-[#D4AF37]/70 bg-[#D4AF37] py-6 text-black hover:bg-[#E5C158]"><UserPlus className="h-4 w-4" /> Add to Turn Order</Button>
          </div>
        )}
      </div>

      <MonsterTemplatePicker addMonsterTemplate={addMonsterTemplate} />

      <Button data-testid="clear-battle-button" onClick={resetBattle} className="w-full border border-[#D4AF37]/35 bg-red-400/5 py-6 text-red-200 hover:bg-red-400/10"><Trash2 className="h-4 w-4" /> Clear Board</Button>
    </aside>
  );
}

async function saveContentEdit({ ownerToken, endpoint, method = "PUT", body, setContentOverrides, successMessage }) {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${ownerToken}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) throw new Error("Could not save content edit.");
  const data = await response.json();
  setContentOverrides({ gods: data.gods || {}, statuses: data.statuses || {} });
  toast.success(successMessage);
}

function OwnerContentTools({ ownerToken, visibleGods, visibleStatuses, setContentOverrides }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedGodId, setSelectedGodId] = useState(visibleGods[0]?.id || "");
  const [selectedStatusId, setSelectedStatusId] = useState(visibleStatuses[0]?.id || "");
  const selectedGod = visibleGods.find((god) => god.id === selectedGodId) || visibleGods[0];
  const selectedStatus = visibleStatuses.find((status) => status.id === selectedStatusId) || visibleStatuses[0];

  return (
    <section className="border border-[#D4AF37]/35 bg-[#111318]/85 p-5 xl:col-span-2" data-testid="owner-content-tools-panel">
      <button type="button" data-testid="toggle-owner-content-tools-button" onClick={() => setIsOpen((value) => !value)} className="flex w-full items-center justify-between text-left">
        <span>
          <span className="block text-xs uppercase tracking-[0.32em] text-[#D4AF37]">Edit Player-Facing Content</span>
          <span className="mt-1 block text-xs text-zinc-500">Owner-only edits for gods, abilities, AC, and statuses</span>
        </span>
        {isOpen ? <ChevronDown className="h-5 w-5 text-[#D4AF37]" /> : <ChevronRight className="h-5 w-5 text-[#D4AF37]" />}
      </button>

      {isOpen && (
        <div className="mt-5 grid gap-5 lg:grid-cols-2" data-testid="owner-content-tools-body">
          <GodEditPanel
            key={selectedGodId}
            ownerToken={ownerToken}
            selectedGod={selectedGod}
            selectedGodId={selectedGodId}
            visibleGods={visibleGods}
            setSelectedGodId={setSelectedGodId}
            setContentOverrides={setContentOverrides}
          />
          <StatusEditPanel
            key={selectedStatusId}
            ownerToken={ownerToken}
            selectedStatus={selectedStatus}
            selectedStatusId={selectedStatusId}
            visibleStatuses={visibleStatuses}
            setSelectedStatusId={setSelectedStatusId}
            setContentOverrides={setContentOverrides}
          />
        </div>
      )}
    </section>
  );
}

function GodEditPanel({ ownerToken, selectedGod, selectedGodId, visibleGods, setSelectedGodId, setContentOverrides }) {
  const [draft, setDraft] = useState(() => selectedGod);

  const updateAbility = (index, field, value) => {
    setDraft((current) => ({
      ...current,
      abilities: current.abilities.map((ability, abilityIndex) => (
        abilityIndex === index ? { ...ability, [field]: value } : ability
      )),
    }));
  };

  const updateUpgrade = (abilityIndex, upgradeIndex, value) => {
    setDraft((current) => ({
      ...current,
      abilities: current.abilities.map((ability, currentAbilityIndex) => (
        currentAbilityIndex === abilityIndex
          ? { ...ability, levels: ability.levels.map((level, currentUpgradeIndex) => (currentUpgradeIndex === upgradeIndex ? value : level)) }
          : ability
      )),
    }));
  };

  const saveGod = async () => {
    try {
      await saveContentEdit({
        ownerToken,
        endpoint: "/owner/content/god",
        body: { god_id: selectedGodId, god: draft },
        setContentOverrides,
        successMessage: `${draft.name} content saved.`,
      });
    } catch (error) {
      toast.error(error.message || "Could not save god edit.");
    }
  };

  const resetGod = async () => {
    try {
      await saveContentEdit({
        ownerToken,
        endpoint: `/owner/content/god/${selectedGodId}`,
        method: "DELETE",
        setContentOverrides,
        successMessage: "God content reset to default.",
      });
      setDraft(GODS.find((god) => god.id === selectedGodId) || selectedGod);
    } catch (error) {
      toast.error(error.message || "Could not reset god edit.");
    }
  };

  if (!draft) return null;

  return (
    <div className="border border-[#D4AF37]/30 bg-black/20 p-4" data-testid="owner-god-edit-panel">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs uppercase tracking-[0.28em] text-[#D4AF37]">God Editor</p>
        <select data-testid="owner-god-edit-select" value={selectedGodId} onChange={(event) => setSelectedGodId(event.target.value)} className="border border-[#D4AF37]/35 bg-black/30 px-3 py-2 text-sm text-zinc-100">
          {visibleGods.map((god) => <option key={god.id} value={god.id}>{god.name}</option>)}
        </select>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <EditableField label="God name" value={draft.name} onChange={(value) => setDraft({ ...draft, name: value })} testId="owner-god-name-input" />
        <EditableField label="AC" value={draft.ac} onChange={(value) => setDraft({ ...draft, ac: value })} testId="owner-god-ac-input" />
        <EditableField className="sm:col-span-2" label="Domain" value={draft.domain} onChange={(value) => setDraft({ ...draft, domain: value })} testId="owner-god-domain-input" />
      </div>
      <div className="mt-4 max-h-96 space-y-4 overflow-y-auto pr-2" data-testid="owner-ability-edit-list">
        {draft.abilities.map((ability, abilityIndex) => (
          <div key={`${ability.name}-${abilityIndex}`} className="border border-[#D4AF37]/25 p-3" data-testid={`owner-ability-edit-${abilityIndex}`}>
            <div className="grid gap-3 sm:grid-cols-2">
              <EditableField label="Ability" value={ability.name} onChange={(value) => updateAbility(abilityIndex, "name", value)} testId={`owner-ability-name-${abilityIndex}`} />
              <EditableField label="Cost" value={ability.cost} onChange={(value) => updateAbility(abilityIndex, "cost", value)} testId={`owner-ability-cost-${abilityIndex}`} />
              <EditableField label="Action" value={ability.action} onChange={(value) => updateAbility(abilityIndex, "action", value)} testId={`owner-ability-action-${abilityIndex}`} />
              <EditableField label="Range" value={ability.range || ""} onChange={(value) => updateAbility(abilityIndex, "range", value)} testId={`owner-ability-range-${abilityIndex}`} />
              <EditableField label="Duration" value={ability.duration || ""} onChange={(value) => updateAbility(abilityIndex, "duration", value)} testId={`owner-ability-duration-${abilityIndex}`} />
              <EditableField className="sm:col-span-2" label="Effect" value={ability.effect} onChange={(value) => updateAbility(abilityIndex, "effect", value)} testId={`owner-ability-effect-${abilityIndex}`} />
            </div>
            <div className="mt-3 grid gap-2">
              {ability.levels.map((upgrade, upgradeIndex) => (
                <EditableField key={upgradeIndex} label={`Level ${upgradeIndex + 2}`} value={upgrade} onChange={(value) => updateUpgrade(abilityIndex, upgradeIndex, value)} testId={`owner-ability-${abilityIndex}-upgrade-${upgradeIndex}`} />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        <Button data-testid="owner-save-god-content-button" onClick={saveGod} className="border border-[#D4AF37]/70 bg-[#D4AF37] text-black hover:bg-[#E5C158]"><Save className="h-4 w-4" /> Save God</Button>
        <Button data-testid="owner-reset-god-content-button" onClick={resetGod} className="border border-[#D4AF37]/35 bg-red-400/5 text-red-200 hover:bg-red-400/10"><RotateCcw className="h-4 w-4" /> Reset God</Button>
      </div>
    </div>
  );
}

function StatusEditPanel({ ownerToken, selectedStatus, selectedStatusId, visibleStatuses, setSelectedStatusId, setContentOverrides }) {
  const [draft, setDraft] = useState(() => selectedStatus);

  const saveStatus = async () => {
    try {
      await saveContentEdit({
        ownerToken,
        endpoint: "/owner/content/status",
        body: { status_id: selectedStatusId, status: draft },
        setContentOverrides,
        successMessage: `${draft.name} status saved.`,
      });
    } catch (error) {
      toast.error(error.message || "Could not save status edit.");
    }
  };

  const resetStatus = async () => {
    try {
      await saveContentEdit({
        ownerToken,
        endpoint: `/owner/content/status/${selectedStatusId}`,
        method: "DELETE",
        setContentOverrides,
        successMessage: "Status reset to default.",
      });
      setDraft(DEFAULT_STATUS_EFFECTS.find((status) => status.id === selectedStatusId) || selectedStatus);
    } catch (error) {
      toast.error(error.message || "Could not reset status edit.");
    }
  };

  if (!draft) return null;

  return (
    <div className="border border-[#D4AF37]/30 bg-black/20 p-4" data-testid="owner-status-edit-panel">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs uppercase tracking-[0.28em] text-[#D4AF37]">Status Editor</p>
        <select data-testid="owner-status-edit-select" value={selectedStatusId} onChange={(event) => setSelectedStatusId(event.target.value)} className="border border-[#D4AF37]/35 bg-black/30 px-3 py-2 text-sm text-zinc-100">
          {visibleStatuses.map((status) => <option key={status.id} value={status.id}>{status.name}</option>)}
        </select>
      </div>
      <div className="mt-4 grid gap-3">
        <EditableField label="Status name" value={draft.name} onChange={(value) => setDraft({ ...draft, name: value })} testId="owner-status-name-input" />
        <EditableField label="Description" value={draft.description} onChange={(value) => setDraft({ ...draft, description: value })} testId="owner-status-description-input" />
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        <Button data-testid="owner-save-status-content-button" onClick={saveStatus} className="border border-[#D4AF37]/70 bg-[#D4AF37] text-black hover:bg-[#E5C158]"><Save className="h-4 w-4" /> Save Status</Button>
        <Button data-testid="owner-reset-status-content-button" onClick={resetStatus} className="border border-[#D4AF37]/35 bg-red-400/5 text-red-200 hover:bg-red-400/10"><RotateCcw className="h-4 w-4" /> Reset Status</Button>
      </div>
    </div>
  );
}

function MonsterTemplatePicker({ addMonsterTemplate }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-[#D4AF37]/35 bg-[#111318]/85" data-testid="monster-template-picker-panel">
      <button
        type="button"
        data-testid="toggle-monster-template-picker-button"
        onClick={() => setIsOpen((current) => !current)}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-[background-color] duration-200 hover:bg-white/[0.03]"
      >
        <span>
          <span className="block text-xs uppercase tracking-[0.32em] text-[#D4AF37]" data-testid="monster-template-picker-title">Monster List</span>
          <span className="mt-1 block text-xs text-zinc-500" data-testid="monster-template-picker-summary">Click a monster to add it with stats</span>
        </span>
        <span className="text-[#D4AF37]" data-testid="monster-template-picker-icon">
          {isOpen ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </span>
      </button>
      {isOpen && (
        <div className="max-h-96 space-y-4 overflow-y-auto border-t border-[#D4AF37]/25 p-5" data-testid="monster-template-area-list">
          {MONSTER_AREAS.map((area) => (
            <section key={area.id} data-testid={`monster-area-${area.id}`}>
              <h3 className="text-xs uppercase tracking-[0.28em] text-[#D4AF37]" data-testid={`monster-area-${area.id}-title`}>{area.name}</h3>
              <div className="mt-3 space-y-2">
                {area.monsters.map((monster) => (
                  <button
                    key={monster.id}
                    type="button"
                    data-testid={`monster-template-${monster.id}-button`}
                    onClick={() => addMonsterTemplate(monster)}
                    className="w-full border border-[#D4AF37]/30 bg-black/20 p-3 text-left transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-[#D4AF37]/60 hover:bg-[#D4AF37]/8"
                  >
                    <span className="flex items-center justify-between gap-3">
                      <span className="font-serif text-xl text-zinc-100" data-testid={`monster-template-${monster.id}-name`}>{monster.name}</span>
                      <span className="text-xs text-zinc-500" data-testid={`monster-template-${monster.id}-stats`}>Init {monster.initiative} · AC {monster.ac} · HP {monster.hp}</span>
                    </span>
                    <span className="mt-2 line-clamp-2 block text-xs leading-5 text-zinc-500" data-testid={`monster-template-${monster.id}-notes`}>{monster.notes}</span>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function TurnOrderTable({ sortedCombatants, currentCombatant, updateCombatant, removeCombatant, visibleStatuses }) {
  const [openCombatantIds, setOpenCombatantIds] = useState([]);

  const toggleCombatant = (id) => {
    setOpenCombatantIds((currentIds) => (
      currentIds.includes(id)
        ? currentIds.filter((currentId) => currentId !== id)
        : [...currentIds, id]
    ));
  };

  const expandAll = () => setOpenCombatantIds(sortedCombatants.map((combatant) => combatant.id));
  const collapseAll = () => setOpenCombatantIds([]);

  return (
    <div className="border border-[#D4AF37]/35 bg-[#111318]/85 p-5" data-testid="turn-order-table-panel">
      <div className="mb-5 flex items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.32em] text-[#D4AF37]" data-testid="turn-order-kicker">Active Order</p>
          <h2 className="mt-2 font-serif text-4xl text-zinc-100" data-testid="turn-order-title">Players & Monsters</h2>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button data-testid="expand-all-combatants-button" onClick={expandAll} className="border border-[#D4AF37]/35 bg-white/[0.03] px-3 py-2 text-xs text-zinc-200 hover:bg-white/10">Expand all</Button>
          <Button data-testid="collapse-all-combatants-button" onClick={collapseAll} className="border border-[#D4AF37]/35 bg-white/[0.03] px-3 py-2 text-xs text-zinc-200 hover:bg-white/10">Collapse all</Button>
          <Badge className="border-[#D4AF37]/35 bg-white/[0.03] text-zinc-400" variant="outline" data-testid="combatant-count-badge">{sortedCombatants.length} combatants</Badge>
        </div>
      </div>

      <div className="space-y-3" data-testid="combatant-list">
        {sortedCombatants.length === 0 && (
          <div className="border border-[#D4AF37]/25 bg-black/20 p-6 text-zinc-500" data-testid="empty-combatant-state">No combatants yet. Add players and monsters to begin.</div>
        )}
        {sortedCombatants.map((combatant, index) => {
          const isCurrent = currentCombatant?.id === combatant.id;
          const isOpen = openCombatantIds.includes(combatant.id);
          return (
            <div key={combatant.id} className={`border ${isCurrent ? "border-[#D4AF37]/80 bg-[#D4AF37]/8" : "border-[#D4AF37]/30 bg-black/20"}`} data-testid={`combatant-row-${combatant.id}`}>
              <button
                type="button"
                data-testid={`toggle-combatant-${combatant.id}-button`}
                onClick={() => toggleCombatant(combatant.id)}
                className="grid w-full gap-2 px-3 py-2.5 text-left transition-[background-color] duration-200 hover:bg-white/[0.03]"
                aria-expanded={isOpen}
              >
                <span className="flex items-center gap-3">
                  <span className="text-[#D4AF37]" data-testid={`combatant-collapse-icon-${combatant.id}`}>{isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</span>
                  <span className="grid h-9 w-10 shrink-0 place-items-center border border-[#D4AF37]/35 font-serif text-xl text-[#D4AF37]" data-testid={`combatant-order-${combatant.id}`}>{index + 1}</span>
                  <span className="min-w-0 flex-1">
                    <span className="line-clamp-1 font-serif text-xl leading-tight text-zinc-100" data-testid={`combatant-summary-name-${combatant.id}`}>{combatant.name}</span>
                  </span>
                  <Badge className="shrink-0 border-[#D4AF37]/35 bg-white/[0.03] text-xs capitalize text-zinc-400" variant="outline" data-testid={`combatant-summary-kind-${combatant.id}`}>{combatant.kind}</Badge>
                  <Badge className="shrink-0 border-[#D4AF37]/35 bg-white/[0.03] text-xs text-zinc-400" variant="outline" data-testid={`combatant-current-badge-${combatant.id}`}>{isCurrent ? "Current" : "View"}</Badge>
                </span>
                <span className="grid gap-2 pl-7 text-xs text-zinc-400 sm:grid-cols-3">
                  <span data-testid={`combatant-summary-initiative-${combatant.id}`}><span className="uppercase tracking-[0.2em] text-zinc-600">Init</span> {combatant.initiative}</span>
                  <span data-testid={`combatant-summary-hp-${combatant.id}`}><span className="uppercase tracking-[0.2em] text-zinc-600">HP</span> {combatant.hp || "—"}</span>
                  <span className="line-clamp-1" data-testid={`combatant-summary-status-notes-${combatant.id}`}><span className="uppercase tracking-[0.2em] text-zinc-600">Status</span> {combatant.statuses || "—"}{combatant.notes ? ` · ${combatant.notes}` : ""}</span>
                </span>
              </button>
              {isOpen && (
                <div className="grid gap-3 border-t border-[#D4AF37]/25 p-4 md:grid-cols-2 xl:grid-cols-4" data-testid={`combatant-details-${combatant.id}`}>
                  <EditableField className="xl:col-span-2" label="Name" value={combatant.name} onChange={(value) => updateCombatant(combatant.id, "name", value)} testId={`combatant-name-${combatant.id}`} />
                  <div>
                    <p className="mb-2 text-xs uppercase tracking-[0.22em] text-zinc-600">Type</p>
                    <select data-testid={`combatant-kind-${combatant.id}`} value={combatant.kind} onChange={(event) => updateCombatant(combatant.id, "kind", event.target.value)} className="w-full border border-[#D4AF37]/35 bg-black/30 px-2 py-2 text-sm text-zinc-100">
                      <option value="player">Player</option>
                      <option value="monster">Monster</option>
                    </select>
                  </div>
                  <EditableField label="Initiative" type="number" value={combatant.initiative} onChange={(value) => updateCombatant(combatant.id, "initiative", value)} testId={`combatant-initiative-${combatant.id}`} />
                  <EditableField label="HP" value={combatant.hp} onChange={(value) => updateCombatant(combatant.id, "hp", value)} testId={`combatant-hp-${combatant.id}`} />
                  <StatusPicker
                    className="md:col-span-2 xl:col-span-2"
                    testId={`combatant-statuses-${combatant.id}`}
                    value={combatant.statuses}
                    statuses={visibleStatuses}
                    onChange={(statuses) => updateCombatant(combatant.id, "statuses", statuses)}
                  />
                  <EditableField className="md:col-span-2 xl:col-span-1" label="Notes" value={combatant.notes} onChange={(value) => updateCombatant(combatant.id, "notes", value)} testId={`combatant-notes-${combatant.id}`} />
                  <Button data-testid={`remove-combatant-${combatant.id}-button`} onClick={() => removeCombatant(combatant.id)} className="h-10 self-end border border-[#D4AF37]/35 bg-red-400/5 px-3 text-red-200 hover:bg-red-400/10">
                    {combatant.kind === "monster" ? <Skull className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EditableField({ label, value, onChange, testId, type = "text", className = "" }) {
  return (
    <label className={`block ${className}`} data-testid={`${testId}-label`}>
      <span className="mb-2 block text-xs uppercase tracking-[0.22em] text-zinc-600">{label}</span>
      <Input data-testid={testId} type={type} value={value} onChange={(event) => onChange(event.target.value)} className="border-[#D4AF37]/35 bg-black/30 text-zinc-100" />
    </label>
  );
}

function StatusPicker({ value, onChange, testId, className = "", statuses = STATUS_EFFECTS }) {
  const selectedStatuses = parseStatuses(value);

  return (
    <div className={`block ${className}`} data-testid={`${testId}-panel`}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="text-xs uppercase tracking-[0.22em] text-zinc-600" data-testid={`${testId}-label`}>Statuses</span>
        <span className="text-xs text-zinc-500" data-testid={`${testId}-selected-count`}>{selectedStatuses.length} selected</span>
      </div>
      <div className="max-h-28 overflow-y-auto border border-[#D4AF37]/35 bg-black/20 p-2" data-testid={`${testId}-scroll-list`}>
        <div className="grid grid-cols-2 gap-2">
          {statuses.map((effect) => {
            const isSelected = selectedStatuses.includes(effect.name);
            return (
              <button
                key={effect.id}
                type="button"
                data-testid={`${testId}-option-${effect.id}`}
                aria-pressed={isSelected}
                onClick={() => onChange(toggleStatusValue(value, effect.name))}
                className={`border px-2 py-1.5 text-left text-xs uppercase tracking-[0.16em] transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 ${isSelected ? "border-[#D4AF37]/70 bg-[#D4AF37]/15 text-[#D4AF37]" : "border-[#D4AF37]/25 bg-white/[0.03] text-zinc-400 hover:border-[#D4AF37]/55"}`}
              >
                {effect.name}
              </button>
            );
          })}
        </div>
      </div>
      <p className="mt-2 line-clamp-1 text-xs text-zinc-500" data-testid={`${testId}-selected-text`}>
        {selectedStatuses.length ? selectedStatuses.join(", ") : "Click statuses to add them."}
      </p>
    </div>
  );
}

function SelectScreen({ playerName, playerLevel, onBack, handleDrawRandom, handleOpenPatron, gods = GODS }) {
  return (
    <section className="py-6" data-testid="god-selection-screen">
      <RegistryHeader playerName={playerName} playerLevel={playerLevel} onBack={onBack} />
      <div className="mb-8 mt-8 grid gap-5 lg:grid-cols-[0.76fr_1.24fr]">
        <div className="space-y-5" data-testid="selection-intro-card">
          <section className="border border-[#D4AF37]/35 bg-[#111318]/85 p-6" data-testid="choose-god-tutorial-panel">
            <h2 className="font-serif text-5xl leading-none text-zinc-100 sm:text-6xl" data-testid="selection-title">Choose Your God</h2>
          </section>
          <Button data-testid="draw-random-god-button" onClick={handleDrawRandom} className="mt-8 w-full border border-[#D4AF37]/50 bg-transparent py-6 text-[#D4AF37] hover:bg-[#D4AF37]/10">
            <Dice5 className="h-5 w-5" /> Draw random god
          </Button>
        </div>

        <div className="grid gap-3 border border-[#D4AF37]/35 bg-black/25 p-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="god-card-grid">
          {gods.map((god) => (
            <button
              key={god.id}
              data-testid={`god-card-${god.id}`}
              onClick={() => handleOpenPatron(god)}
              className="group min-h-44 border border-[#D4AF37]/35 bg-[#141414]/80 p-5 text-left transition-[transform,border-color,background-color] duration-300 hover:-translate-y-1 hover:border-[#D4AF37]/70 hover:bg-[#191919] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]/60"
            >
              <div className="mb-5 flex items-start justify-between gap-3">
                <GodSymbol god={god} testId={`god-card-${god.id}-symbol`} className="h-14 w-14 rounded-2xl" iconClassName="h-7 w-7" />
                <Badge className="border-white/10 bg-white/[0.03] text-zinc-400" variant="outline" data-testid={`god-card-${god.id}-ac`}>{god.ac}</Badge>
              </div>
              <h3 className="font-serif text-2xl text-zinc-100" data-testid={`god-card-${god.id}-name`}>{god.name}</h3>
              <p className="mt-2 text-sm leading-6 text-zinc-500" data-testid={`god-card-${god.id}-domain`}>{god.domain}</p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function LockedScreen({
  playerName,
  playerLevel,
  lockedGod,
  activeStatus,
  statuses,
  visibleGods,
  visibleStatuses,
  availableUpgrades,
  onBack,
  handleLevelChange,
  handleLevelDelta,
  handleOpenReroll,
  setActiveStatusId,
  ownerToken,
  setContentOverrides,
}) {
  const playerLevelSliderValue = useMemo(() => [playerLevel], [playerLevel]);
  const [editMode, setEditMode] = useState(null);

  return (
    <section className="py-6" data-testid="locked-patron-screen">
      <RegistryHeader playerName={playerName} playerLevel={playerLevel} locked onBack={onBack} />
      <div className="mt-8 grid gap-6 lg:grid-cols-[0.82fr_1.18fr]">
        <aside className="space-y-6" data-testid="patron-summary-column">
          <div className="overflow-hidden border border-[#D4AF37]/35 bg-[#141414]" data-testid="locked-god-card">
            <div className="h-2" style={{ backgroundColor: lockedGod.color }} data-testid="locked-god-color-strip" />
            <div className="p-7">
              <div className="mb-6 flex items-center justify-between">
                <GodSymbol god={lockedGod} testId="locked-god-symbol" className="h-20 w-20 rounded-3xl" iconClassName="h-10 w-10" />
                <div className="flex flex-col items-end gap-2">
                  <Badge className="border-[#D4AF37]/45 bg-emerald-400/10 text-emerald-200" variant="outline" data-testid="locked-status-badge">
                    <Lock className="mr-1 h-3.5 w-3.5" /> Locked
                  </Badge>
                  {ownerToken && (
                    <Button data-testid="edit-locked-god-button" onClick={() => setEditMode("god")} className="h-8 border border-[#D4AF37]/35 bg-white/[0.03] px-3 text-xs text-[#D4AF37] hover:bg-[#D4AF37]/10">
                      <Settings className="h-3.5 w-3.5" /> Edit God
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs uppercase tracking-[0.32em] text-zinc-500" data-testid="locked-patron-label">Your patron</p>
              <h1 className="mt-3 font-serif text-6xl leading-none text-zinc-100" data-testid="locked-god-name">{lockedGod.name}</h1>
              <p className="mt-4 text-base leading-7 text-zinc-400" data-testid="locked-god-domain">{lockedGod.domain}</p>
              <div className="mt-7 grid grid-cols-2 gap-3">
                <div className="border border-[#D4AF37]/35 bg-black/30 p-4" data-testid="locked-god-ac-panel">
                  <p className="text-xs uppercase tracking-[0.24em] text-zinc-600">Armor</p>
                  <p className="mt-2 font-serif text-3xl" style={{ color: lockedGod.color }} data-testid="locked-god-ac">{lockedGod.ac}</p>
                </div>
                <div className="border border-[#D4AF37]/35 bg-black/30 p-4" data-testid="locked-god-upgrades-panel">
                  <p className="text-xs uppercase tracking-[0.24em] text-zinc-600">Unlocked</p>
                  <p className="mt-2 font-serif text-3xl text-[#D4AF37]" data-testid="unlocked-upgrades-count">{availableUpgrades}/4</p>
                </div>
              </div>
            </div>
          </div>

          <div className="border border-[#D4AF37]/35 bg-[#141414]/80 p-6" data-testid="level-control-card">
            <div className="mb-5 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.28em] text-zinc-500" data-testid="level-control-label">Player level</p>
                <p className="mt-1 text-sm text-zinc-500" data-testid="level-control-help">Changes unlock ability upgrades.</p>
              </div>
              <span className="font-serif text-4xl text-[#D4AF37]" data-testid="current-player-level">{playerLevel}</span>
            </div>
            <div className="flex items-center gap-3" data-testid="level-stepper">
              <Button data-testid="decrease-level-button" onClick={() => handleLevelDelta(-1)} disabled={playerLevel <= 1} className="h-12 w-12 border border-[#D4AF37]/35 bg-white/[0.03] p-0 text-zinc-100 hover:bg-white/10">
                <Minus className="h-4 w-4" />
              </Button>
              <Slider data-testid="locked-level-slider" value={playerLevelSliderValue} min={1} max={5} step={1} onValueChange={([value]) => handleLevelChange(value)} className="flex-1 [&_[role=slider]]:border-[#D4AF37] [&_[role=slider]]:bg-[#D4AF37] [&_[data-orientation=horizontal]>span]:bg-[#D4AF37]" />
              <Button data-testid="increase-level-button" onClick={() => handleLevelDelta(1)} disabled={playerLevel >= 5} className="h-12 w-12 border border-[#D4AF37]/35 bg-white/[0.03] p-0 text-zinc-100 hover:bg-white/10">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <Button data-testid="open-reroll-dialog-button" onClick={handleOpenReroll} className="w-full border border-[#D4AF37]/35 bg-red-400/5 py-6 text-red-200 hover:bg-red-400/10">
            <RotateCcw className="h-4 w-4" /> Reroll patron
          </Button>
          <StatusEffectsPanel activeStatus={activeStatus} setActiveStatusId={setActiveStatusId} statuses={statuses} ownerToken={ownerToken} onEditStatus={() => setEditMode("status")} />
        </aside>

        <AbilitiesPanel lockedGod={lockedGod} playerLevel={playerLevel} ownerToken={ownerToken} onEditGod={() => setEditMode("god")} />
      </div>
      <Dialog key={editMode || "closed-inline-editor"} open={Boolean(editMode)} onOpenChange={(open) => !open && setEditMode(null)}>
        <DialogContent closeTestId="content-edit-close-button" className="max-h-[88vh] overflow-y-auto border-[#D4AF37]/45 bg-black/90 text-zinc-100 sm:max-w-5xl" data-testid="inline-content-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-4xl" data-testid="inline-content-edit-title">Owner Edit</DialogTitle>
            <DialogDescription className="text-zinc-400" data-testid="inline-content-edit-description">Changes save to the database and update what players see.</DialogDescription>
          </DialogHeader>
          {editMode === "god" && (
            <GodEditPanel key={lockedGod.id} ownerToken={ownerToken} selectedGod={lockedGod} selectedGodId={lockedGod.id} visibleGods={visibleGods} setSelectedGodId={() => {}} setContentOverrides={setContentOverrides} />
          )}
          {editMode === "status" && (
            <StatusEditPanel key={activeStatus.id} ownerToken={ownerToken} selectedStatus={activeStatus} selectedStatusId={activeStatus.id} visibleStatuses={visibleStatuses} setSelectedStatusId={() => {}} setContentOverrides={setContentOverrides} />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function AbilitiesPanel({ lockedGod, playerLevel, ownerToken, onEditGod }) {
  return (
    <div className="border border-[#D4AF37]/35 bg-[#141414]/80 p-5 sm:p-7" data-testid="abilities-panel">
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="text-xs uppercase tracking-[0.32em] text-[#D4AF37]" data-testid="abilities-kicker">Ability Examples</p>
          {ownerToken && (
            <Button data-testid="edit-abilities-button" onClick={onEditGod} className="mt-2 h-8 border border-[#D4AF37]/35 bg-white/[0.03] px-3 text-xs text-[#D4AF37] hover:bg-[#D4AF37]/10">
              <Settings className="h-3.5 w-3.5" /> Edit Abilities
            </Button>
          )}
        </div>
        <Badge className="w-fit border-[#D4AF37]/35 bg-white/[0.03] px-3 py-1.5 text-zinc-400" variant="outline" data-testid="ability-count-badge">
          <BookOpenText className="mr-1 h-3.5 w-3.5" /> {lockedGod.abilities.length} abilities
        </Badge>
      </div>

      <Accordion type="single" defaultValue="ability-0" className="space-y-3" data-testid="abilities-accordion">
        {lockedGod.abilities.map((ability, index) => (
          <AbilityItem key={ability.name} ability={ability} index={index} playerLevel={playerLevel} />
        ))}
      </Accordion>
    </div>
  );
}

function AbilityItem({ ability, index, playerLevel }) {
  const unlocked = ability.levels.filter((_, levelIndex) => levelIndex + 2 <= playerLevel);

  return (
    <AccordionItem value={`ability-${index}`} className="overflow-hidden border border-[#D4AF37]/35 bg-black/20 px-4" data-testid={`ability-item-${index}`}>
      <AccordionTrigger data-testid={`ability-trigger-${index}`} className="gap-4 py-5 text-left hover:no-underline">
        <div className="min-w-0 flex-1">
          <h3 className="font-serif text-2xl text-zinc-100" data-testid={`ability-name-${index}`}>{ability.name}</h3>
          <p className="mt-1 line-clamp-1 text-sm text-zinc-500" data-testid={`ability-preview-${index}`}>{ability.effect}</p>
        </div>
        <Badge className={`${actionTone(ability.action)} shrink-0`} variant="outline" data-testid={`ability-action-${index}`}>{ability.action}</Badge>
      </AccordionTrigger>
      <AccordionContent className="pb-5" data-testid={`ability-content-${index}`}>
        <div className="grid gap-3 border-t border-white/10 pt-5 sm:grid-cols-3" data-testid={`ability-meta-${index}`}>
          <Meta label="Cost" value={ability.cost} testId={`ability-cost-${index}`} />
          {ability.range && <Meta label="Range" value={ability.range} testId={`ability-range-${index}`} />}
          {ability.duration && <Meta label="Duration" value={ability.duration} testId={`ability-duration-${index}`} />}
        </div>
        <p className="mt-5 border border-[#D4AF37]/35 bg-white/[0.03] p-4 text-base leading-8 text-zinc-300" data-testid={`ability-effect-${index}`}>{ability.effect}</p>
        <div className="mt-5 space-y-3" data-testid={`ability-upgrades-${index}`}>
          {ability.levels.map((upgrade, levelIndex) => (
            <UpgradeRow key={upgrade} upgrade={upgrade} abilityIndex={index} requiredLevel={levelIndex + 2} playerLevel={playerLevel} />
          ))}
        </div>
        <p className="sr-only" data-testid={`ability-unlocked-count-${index}`}>{unlocked.length} upgrades unlocked</p>
      </AccordionContent>
    </AccordionItem>
  );
}

function UpgradeRow({ upgrade, abilityIndex, requiredLevel, playerLevel }) {
  const isUnlocked = requiredLevel <= playerLevel;

  return (
    <div className={`flex gap-3 border p-4 ${isUnlocked ? "border-[#D4AF37]/45 bg-[#D4AF37]/5" : "border-[#D4AF37]/25 bg-black/20 opacity-55"}`} data-testid={`ability-upgrade-${abilityIndex}-${requiredLevel}`}>
      <div className={`grid h-8 w-8 shrink-0 place-items-center border text-sm ${isUnlocked ? "border-[#D4AF37]/55 text-[#D4AF37]" : "border-[#D4AF37]/25 text-zinc-600"}`} data-testid={`ability-upgrade-level-${abilityIndex}-${requiredLevel}`}>
        {isUnlocked ? <Check className="h-4 w-4" /> : requiredLevel}
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.24em] text-zinc-500" data-testid={`ability-upgrade-status-${abilityIndex}-${requiredLevel}`}>{isUnlocked ? `Level ${requiredLevel} unlocked` : `Unlocks at level ${requiredLevel}`}</p>
        <p className="mt-1 text-sm leading-6 text-zinc-300" data-testid={`ability-upgrade-text-${abilityIndex}-${requiredLevel}`}>
          {isUnlocked ? upgrade : "Upgrade hidden until you reach this level."}
        </p>
      </div>
    </div>
  );
}

function RegistryHeader({ playerName, playerLevel, locked = false, onBack }) {
  return (
    <header className="flex flex-col gap-4 border-b border-white/10 pb-5 sm:flex-row sm:items-center sm:justify-between" data-testid="registry-header">
      <div className="flex items-center gap-3">
        <div className="grid h-11 w-11 place-items-center border border-[#D4AF37]/45 bg-[#D4AF37]/10 text-[#D4AF37]" data-testid="registry-header-icon">
          {locked ? <Lock className="h-5 w-5" /> : <Feather className="h-5 w-5" />}
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.35em] text-[#D4AF37]" data-testid="registry-header-title">Arcaeus</p>
          <p className="text-sm text-zinc-500" data-testid="registry-header-subtitle">God Registry</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Badge variant="outline" className="border-[#D4AF37]/35 bg-white/[0.03] px-3 py-1.5 text-zinc-300" data-testid="header-player-badge">
          <Swords className="mr-1 h-3.5 w-3.5" /> {playerName} · Lv {playerLevel}
        </Badge>
        <Button data-testid="back-to-entry-button" onClick={onBack} className="border border-[#D4AF37]/35 bg-white/[0.03] text-zinc-200 hover:bg-white/10">
          <ArrowLeft className="h-4 w-4" /> {locked ? "Exit" : "Back"}
        </Button>
      </div>
    </header>
  );
}

function TutorialTitle() {
  return (
    <header className="border border-[#D4AF37]/40 bg-[#111318]/90 p-8" data-testid="tutorial-title-panel">
      <h1 className="max-w-4xl font-serif text-6xl font-semibold leading-[0.92] text-zinc-100 sm:text-7xl lg:text-8xl" data-testid="app-title">
        Arcaeus
      </h1>
      <p className="mt-4 max-w-2xl text-sm uppercase tracking-[0.28em] text-[#D4AF37]" data-testid="app-description">
        God Registry
      </p>
    </header>
  );
}

function RandomGodReveal({ god, phase }) {
  if (!god) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-md" data-testid="random-god-reveal-overlay">
      <div className="arcaeus-card-stage" data-testid="random-god-reveal-stage">
        <div className={`arcaeus-reveal-card ${phase === "back" ? "is-back" : "is-flipped"} ${phase === "zooming" ? "is-zooming" : ""}`} data-testid="random-god-reveal-card">
          <div className="arcaeus-reveal-face arcaeus-reveal-back" data-testid="random-god-card-back">
            <div className="border border-[#D4AF37]/45 bg-[#111318]/95 p-7 text-center">
              <p className="text-xs uppercase tracking-[0.45em] text-[#D4AF37]" data-testid="random-card-back-kicker">Arcaeus</p>
              <div className="mx-auto my-10 grid h-28 w-28 place-items-center border border-[#D4AF37]/50 text-[#D4AF37]">
                <Crown className="h-14 w-14" />
              </div>
              <p className="font-serif text-3xl text-zinc-100" data-testid="random-card-back-title">The Oath Turns</p>
            </div>
          </div>
          <div className="arcaeus-reveal-face arcaeus-reveal-front" data-testid="random-god-card-front">
            <div className="border border-[#D4AF37]/55 bg-[#111318]/95 p-7 text-center shadow-2xl shadow-black/50">
              <p className="text-xs uppercase tracking-[0.42em] text-[#D4AF37]" data-testid="random-card-front-kicker">Chosen Patron</p>
              <GodSymbol god={god} testId="random-reveal-god-symbol" className="mx-auto my-8 h-28 w-28" iconClassName="h-14 w-14" />
              <h2 className="font-serif text-6xl leading-none text-zinc-100" data-testid="random-reveal-god-name">{god.name}</h2>
              <p className="mt-4 text-base leading-7 text-zinc-400" data-testid="random-reveal-god-domain">{god.domain}</p>
              <p className="mt-8 text-xs uppercase tracking-[0.3em] text-zinc-500" data-testid="random-reveal-transition-text">Opening divine abilities...</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CombatPanel({ title, testId, children }) {
  return (
    <section className="border border-[#D4AF37]/35 bg-[#111318]/85 p-5 shadow-[inset_0_1px_0_rgba(212,175,55,0.12)]" data-testid={testId}>
      <div className="mb-4 flex items-center gap-3">
        <span className="h-px w-8 bg-[#D4AF37]/60" />
        <h2 className="text-xs uppercase tracking-[0.34em] text-[#D4AF37]" data-testid={`${testId}-title`}>{title}</h2>
      </div>
      {children}
    </section>
  );
}

function GodSymbol({ god, testId, className = "h-14 w-14", iconClassName = "h-7 w-7" }) {
  const Icon = GOD_ICON_MAP[god.id] || Crown;

  return (
    <div
      className={`grid shrink-0 place-items-center border bg-black/40 ${className}`}
      style={{ borderColor: `${god.color}80`, color: god.color }}
      data-testid={testId}
      aria-label={`${god.name} symbol`}
    >
      <Icon className={iconClassName} strokeWidth={1.7} />
    </div>
  );
}

function StatusEffectsPanel({ activeStatus, setActiveStatusId, statuses = STATUS_EFFECTS, ownerToken, onEditStatus }) {
  return (
    <CombatPanel title="Conditions" testId="status-effects-panel">
      {ownerToken && (
        <Button data-testid="edit-statuses-button" onClick={onEditStatus} className="mb-3 h-8 border border-[#D4AF37]/35 bg-white/[0.03] px-3 text-xs text-[#D4AF37] hover:bg-[#D4AF37]/10">
          <Settings className="h-3.5 w-3.5" /> Edit Statuses
        </Button>
      )}
      <div className="grid grid-cols-2 gap-2" data-testid="status-effects-grid">
        {statuses.map((effect) => (
          <button
            type="button"
            key={effect.id}
            data-testid={`status-effect-${effect.id}-button`}
            aria-pressed={activeStatus.id === effect.id}
            onPointerDown={() => setActiveStatusId(effect.id)}
            onClick={() => setActiveStatusId(effect.id)}
            className={`min-h-10 border px-3 py-2 text-left text-xs uppercase tracking-[0.18em] transition-[transform,border-color,background-color] duration-200 hover:-translate-y-0.5 active:scale-[0.98] ${activeStatus.id === effect.id ? effect.tone : "border-[#D4AF37]/30 bg-white/[0.03] text-zinc-400 hover:border-[#D4AF37]/60"}`}
          >
            {effect.name}
          </button>
        ))}
      </div>
      <div className={`mt-4 border p-4 ${activeStatus.tone}`} data-testid="active-status-description-card">
        <div className="flex items-center gap-2">
          {activeStatus.id === "burning" ? <Flame className="h-4 w-4" /> : <CircleAlert className="h-4 w-4" />}
          <h3 className="font-serif text-2xl" data-testid="active-status-name">{activeStatus.name}</h3>
        </div>
        <p className="mt-2 text-sm normal-case leading-6 tracking-normal text-zinc-200" data-testid="active-status-description">{activeStatus.description}</p>
      </div>
    </CombatPanel>
  );
}

function Meta({ label, value, testId }) {
  return (
    <div className="border border-[#D4AF37]/35 bg-black/20 p-4" data-testid={`${testId}-panel`}>
      <p className="text-xs uppercase tracking-[0.24em] text-zinc-600" data-testid={`${testId}-label`}>{label}</p>
      <p className="mt-1 text-sm font-medium text-zinc-200" data-testid={testId}>{value}</p>
    </div>
  );
}

export default App;
